import React, {useContext, useEffect, useState} from 'react';
import './StakePage.scss';
import StakeTabsNavigation from "./StakeTabs/StakeTabsNavigation";
import StakeButtons from "./StakeButtons/StakeButtons";
import {stakeBalanceStore} from "../../stores/StakeBalanceStore";
import {depositWithdrawalStakeStore} from "../../services/deposit-withdrawal-stake/deposit-withdrawal-stake.store";
import {useAccount, useBlockNumber} from "wagmi";
import {ContractContext} from "../../context-providers/ContractsProvider";
import {useBreakpointContext} from "../../context-providers/BreakpointProvider";
import apiService from "../../services/api/api.service";

const secondsInYear = 365n * 24n * 60n * 60n

const StakePage = () => {
    const { xs, md } = useBreakpointContext();
    const { data: blockNumber } = useBlockNumber({ watch: true });
    const { address: userAddress } = useAccount();
    const { nrsContract, veNrsContract, nrsRewardControllerContract, nrsWithdrawalTimelockContract } = useContext(ContractContext);
    const [apr, setApr] = useState('')
    const [yearlyEarnEstimate, setYearlyEarnEstimate] = useState('')
    const [totalEarned, setTotalEarned] = useState('')

    useEffect(() => {
        async function fetch() {
            if (userAddress) {
                const earned = await apiService.stakingEarnedGet(userAddress);
                if (earned.totalEarned) {
                    const totalEarned = BigInt(earned.totalEarned) / 10n ** 18n
                    setTotalEarned(totalEarned.toString())
                }
            }
        }
        fetch()
    }, [userAddress]);

    useEffect(() => {
        if (!userAddress) {
            stakeBalanceStore.resetState()
            depositWithdrawalStakeStore.resetState()
        }
    }, [userAddress]);

    useEffect(() => {
        if (userAddress && blockNumber !== undefined) {
            stakeBalanceStore.fetchBalancesThrottled(nrsContract, veNrsContract, userAddress);
            depositWithdrawalStakeStore.fetchWithdrawalsThrottled(nrsWithdrawalTimelockContract, userAddress)
        }
    }, [userAddress, nrsContract, veNrsContract, nrsWithdrawalTimelockContract, blockNumber]);

    useEffect(() => {
        async function fetch() {
            try {
                if (nrsRewardControllerContract && veNrsContract) {
                    const rewardsEnabled = await nrsRewardControllerContract.read.rewardsEnabled()
                    if (!rewardsEnabled) {
                        return;
                    }

                    const rewardsRate = await nrsRewardControllerContract.read.rewardsRate()
                    const rewardsPeriod = await nrsRewardControllerContract.read.rewardsPeriod()
                    const totalAssets = await veNrsContract.read.totalAssets()

                    if (totalAssets > 0) {
                        const precision = 100n
                        const apr = secondsInYear * rewardsRate * 100n * precision / rewardsPeriod / totalAssets
                        setApr(`${Number((Number(apr) / Number(precision)).toFixed(2))}%`)

                        const estimate = BigInt(stakeBalanceStore.nrsAvailableBalance) * apr / 100n / precision / 10n ** 18n
                        setYearlyEarnEstimate(estimate.toString())
                    }

                }
            } catch (err) {
                console.error(err)
            }
        }
        fetch()
    }, [userAddress, veNrsContract, nrsRewardControllerContract, stakeBalanceStore.nrsAvailableBalance]);

    return (
        <div className="StakePage content-container justify-start flex-col flex-grow">
            {md && (
                <div className="StakePage__header flex md">
                    <div className="stats card-container flex">
                        <div className="stats__apy flex-col">
                            <label className="nereus-caption">APR</label>
                            <div className={`nereus-body-m`}>{apr}</div>
                        </div>
                        <div className="flex-col">
                            <label className="nereus-caption nereus-color-neutral-grey">Yearly earn NRS</label>
                            <div className={`nereus-body-m`}>{yearlyEarnEstimate}</div>
                        </div>
                        <div className="flex-col">
                            <label className="nereus-caption nereus-color-neutral-grey">Total earned NRS</label>
                            <div className={`nereus-body-m`}>{totalEarned}</div>
                        </div>
                    </div>
                    <div className="balances card-container flex">
                        <div className="flex-col">
                            <label className="nereus-caption nereus-color-neutral-grey">Wallet&nbsp;Balance NRS</label>
                            <div className={`nereus-body-m`}>{stakeBalanceStore.nrsTokenBalanceDecimal}</div>
                        </div>
                        <div className="flex-col">
                            <label className="nereus-caption nereus-color-neutral-grey">Staked&nbsp;NRS</label>
                            <div className={`nereus-body-m`}>{stakeBalanceStore.nrsAvailableBalanceDecimal}</div>
                        </div>
                        <div className="buttons flex flex-grow align-item">
                            <StakeButtons/>
                        </div>
                    </div>
                </div>
            )}
            {!md && !xs && (
                <div className="StakePage__header flex flex-col">
                    <div className="balances card-container flex">
                        <div className="flex-col">
                            <label className="nereus-caption nereus-color-neutral-grey">Wallet&nbsp;Balance&nbsp;NRS</label>
                            <div className={`nereus-body-m`}>{stakeBalanceStore.nrsTokenBalanceDecimal}</div>
                        </div>
                        <div className="flex-col">
                            <label className="nereus-caption nereus-color-neutral-grey">Staked&nbsp;NRS</label>
                            <div className={`nereus-body-m`}>{stakeBalanceStore.nrsAvailableBalanceDecimal}</div>
                        </div>
                        <div className="buttons flex flex-grow">
                            <StakeButtons/>
                        </div>
                    </div>
                    <div className="stats card-container flex">
                    <div className="stats__apy flex-col">
                            <label className="nereus-caption">APR</label>
                            <div className={`nereus-body-m`}>{apr}</div>
                        </div>
                        <div className="flex-col">
                            <label className="nereus-caption nereus-color-neutral-grey">Yearly earn NRS</label>
                            <div className={`nereus-body-m`}>{yearlyEarnEstimate}</div>
                        </div>
                        <div className="flex-col">
                            <label className="nereus-caption nereus-color-neutral-grey">Total earned NRS</label>
                            <div className={`nereus-body-m`}>{totalEarned}</div>
                        </div>
                    </div>
                </div>
            )}
            {xs && (
                <div className="StakePage__header flex flex-col">
                    <div className="balances card-container flex-col">
                        <div className="flex">
                            <div className="flex-col">
                                <label className="nereus-caption nereus-color-neutral-grey">Wallet Balance NRS</label>
                                <div className={`nereus-body-m`}>{stakeBalanceStore.nrsTokenBalanceDecimal}</div>
                            </div>
                            <div className="flex-col">
                                <label className="nereus-caption nereus-color-neutral-grey">Staked NRS</label>
                                <div className={`nereus-body-m`}>{stakeBalanceStore.nrsAvailableBalanceDecimal}</div>
                            </div>
                        </div>
                        <div className="buttons flex flex-grow">
                            <StakeButtons/>
                        </div>
                    </div>
                    <div className="stats card-container flex">
                        <div className="stats__apy flex-col">
                        <label className="nereus-caption">APR</label>
                            <div className={`nereus-body-m`}>{apr}</div>
                        </div>
                        <div className="flex-col">
                            <label className="nereus-caption nereus-color-neutral-grey">Yearly earn NRS</label>
                            <div className={`nereus-body-m`}>{yearlyEarnEstimate}</div>
                        </div>
                        <div className="flex-col">
                            <label className="nereus-caption nereus-color-neutral-grey">Total earned NRS</label>
                            <div className={`nereus-body-m`}>{totalEarned}</div>
                        </div>
                    </div>
                </div>
            )}
            <StakeTabsNavigation/>
        </div>
    );
}

export default StakePage;